import { IconButton, TextInput } from '@dev-spendesk/grapes';
import { useRef, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { normalizeString } from 'src/core/common/utils/normalizeString';

import { useSearchAndFocusHotkey } from './useSearchAndFocusHotkey';
import { useZoomTo, ZOOM_DURATION } from './useZoomTo';
import { type ApprovalSchemeWithMemberNames } from '../../hooks/useApprovalSchemesQuery';
import { useWorkflowContext } from '../../state/WorkflowContext';

const DEBOUNCE_DELAY = 500;

export const NodeSearch = () => {
  const inputReference = useRef<HTMLInputElement>(null);
  const searchActiveReference = useRef(false);
  useSearchAndFocusHotkey(inputReference); // hotkey ctrl+f or cmd+f and focus on the input
  const { t } = useTranslation('global');
  const {
    setSelectedNodeId,
    selectedNodeId,
    nodeSearchQuery,
    setNodeSearchQuery,
    nodes,
  } = useWorkflowContext();
  const [currentNodeIndex, setCurrentNodeIndex] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState(nodeSearchQuery);
  const [noResults, setNoResults] = useState(false);
  useDebounce(
    () => {
      setDebouncedSearch(nodeSearchQuery);
    },
    DEBOUNCE_DELAY,
    [nodeSearchQuery],
  );
  const zoomTo = useZoomTo();

  useEffect(() => {
    setCurrentNodeIndex(0);
    if (debouncedSearch.trim() === '') {
      setNoResults(false);
    }
  }, [debouncedSearch]);

  const filteredNodes = useMemo(() => {
    if (debouncedSearch.trim() === '') {
      return [];
    }

    const normalizedSearch = normalizeString(debouncedSearch);
    return nodes.filter((node) => {
      if (node.type !== 'baseNode') {
        return false;
      }

      const valuesNames =
        node.data.values?.map((value) => value.name).join(' ') ?? '';
      const approverNames = getApproverNames(node.data.approvalScheme).join(
        ' ',
      );
      const valuesToSearch = [valuesNames, approverNames]
        .filter(Boolean)
        .join(' ');

      if (valuesToSearch.trim() === '') {
        return false;
      }

      return normalizeString(valuesToSearch).includes(normalizedSearch);
    });
  }, [debouncedSearch, nodes]);

  useEffect(() => {
    if (debouncedSearch.trim() === '') {
      setNoResults(false);
      setCurrentNodeIndex(0);
      searchActiveReference.current = false;
      return;
    }

    searchActiveReference.current = true;
    setNoResults(filteredNodes.length === 0);

    if (filteredNodes.length === 0) {
      return;
    }

    const safeIndex = Math.min(currentNodeIndex, filteredNodes.length - 1);
    setCurrentNodeIndex(safeIndex);
    zoomTo(
      filteredNodes[safeIndex].position.x,
      filteredNodes[safeIndex].position.y,
    );
    setTimeout(() => {
      // only select if the search is still active
      if (searchActiveReference.current) {
        setSelectedNodeId(filteredNodes[safeIndex].id);
      }
    }, ZOOM_DURATION);
  }, [
    filteredNodes,
    setSelectedNodeId,
    zoomTo,
    debouncedSearch,
    selectedNodeId,
    currentNodeIndex,
  ]);

  const handlePrevious = () => {
    if (currentNodeIndex > 0) {
      const newIndex = currentNodeIndex - 1;
      setCurrentNodeIndex(newIndex);
      zoomTo(
        filteredNodes[newIndex].position.x,
        filteredNodes[newIndex].position.y,
      );
      setTimeout(() => {
        setSelectedNodeId(filteredNodes[newIndex].id);
      }, ZOOM_DURATION);
    }
  };
  const handleNext = () => {
    if (currentNodeIndex < filteredNodes.length - 1) {
      const newIndex = currentNodeIndex + 1;
      setCurrentNodeIndex(newIndex);
      zoomTo(
        filteredNodes[newIndex].position.x,
        filteredNodes[newIndex].position.y,
      );
      setTimeout(() => {
        setSelectedNodeId(filteredNodes[newIndex].id);
      }, ZOOM_DURATION);
    }
  };

  return (
    <div className="flex items-center gap-8">
      <TextInput
        ref={inputReference}
        value={nodeSearchQuery}
        onChange={(event) => setNodeSearchQuery(event.target.value)}
        className="min-w-[280px]"
        placeholder={t('approvalWorkflows.searchPlaceholder')}
        rightAddon={
          nodeSearchQuery !== '' && (
            <IconButton
              iconName="cross"
              onClick={() => setNodeSearchQuery('')}
              aria-label={t('approvalWorkflows.clearSearch')}
            />
          )
        }
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            handleNext();
          }
        }}
      />
      {noResults && (
        <div className="flex items-center gap-8">
          <div className="text-content-secondary-bg-secondary">
            {t('approvalWorkflows.search.noResults')}
          </div>
        </div>
      )}
      {filteredNodes.length > 1 && (
        <div className="flex items-center gap-8">
          <div className="text-content-secondary-bg-secondary">
            {t('approvalWorkflows.search.results', {
              currentNodeIndex: currentNodeIndex + 1,
              filteredNodesLength: filteredNodes.length,
            })}
          </div>
          <IconButton
            aria-label="previous"
            iconName="arrow-left"
            variant="secondaryNeutral"
            isDisabled={currentNodeIndex === 0}
            onClick={handlePrevious}
          />
          <IconButton
            aria-label="next"
            iconName="arrow-right"
            variant="secondaryNeutral"
            isDisabled={currentNodeIndex === filteredNodes.length - 1}
            onClick={handleNext}
          />
        </div>
      )}
    </div>
  );
};

const getApproverNames = (
  approvalScheme?: ApprovalSchemeWithMemberNames,
): string[] => {
  if (!approvalScheme) {
    return [];
  }

  return approvalScheme.rules.flatMap((rule) =>
    rule.steps.flatMap((step) =>
      ((step.rights ?? [])).flatMap((right) => {
        if (right.approverType !== 'user' || !right.approverName) {
          return [];
        }

        return [right.approverName];
      }),
    ),
  );
};
